import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { PartOrderLine, ReceiveLineRequest } from '@techgitter/contracts';
import { receiveLine } from '../../services/partOrdersApi';
import { getProducts } from '../../services/productCacheService';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { DateField } from '../dropdown';
import './ReceiveLinePanel.css';

interface Props {
  line: PartOrderLine;
  onClose: (received: boolean) => void;
}

/** Upper bound on labels printed on receipt — mirrors the API's MAX_RECEIPT_LABELS. */
const MAX_RECEIPT_LABELS = 10;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Default label count for a received qty: one per unit, capped at the max. */
function defaultLabelCount(qty: number): number {
  if (!Number.isFinite(qty) || qty <= 0) return 0;
  return Math.min(Math.floor(qty), MAX_RECEIPT_LABELS);
}

/** Split a pasted block into trimmed, non-empty key lines. */
function parseKeys(raw: string): string[] {
  return raw
    .split('\n')
    .map((k) => k.trim())
    .filter(Boolean);
}

/**
 * Right-aligned panel for recording a receipt against a single part-order line.
 * Reuses the shared `SidePanel` shell + `FormField` controls (the same chrome as
 * `AddStockLinePanel` and the calendar panels), so it reads identically to the
 * rest of the app's right-column forms. Lives in the right column of
 * PartOrdersPage's `.part-orders-layout` grid — replacing the former modal so the
 * underlying ordered-lines table stays visible (see sm4rt-web-ui §6/§7).
 *
 * Defaults the qty to the outstanding remainder (qty - receivedQty) so
 * single-shipment receipts complete the line in one click.
 */
export const ReceiveLinePanel: React.FC<Props> = ({ line, onClose }) => {
  const outstanding = Math.max(0, line.qty - (line.receivedQty || 0));
  const [receivedQty, setReceivedQty] = useState<string>(String(outstanding || line.qty));
  const [receivedDate, setReceivedDate] = useState(todayIso());
  const [actualUnitCost, setActualUnitCost] = useState<string>(
    line.actualUnitCost != null
      ? String(line.actualUnitCost)
      : line.expectedUnitCost != null
        ? String(line.expectedUnitCost)
        : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Label printing: on receive, AutoPrint prints one part-receipt CUSTOMER label
  // per received unit (capped at MAX_RECEIPT_LABELS) to attach to the arriving
  // parts. The label is keyed to the customer (name/phone/email + a QR to the
  // order), so it applies to any line with a customer; stock / internal-use
  // lines have none, so the field is hidden for them. This field overrides the
  // copies for this receipt (auto-tracks Received Qty until edited; 0 = don't
  // print).
  const offersLabels =
    line.triggeredBy !== 'stock' && line.triggeredBy !== 'internal_use' && !!line.customerName;
  const [labelCount, setLabelCount] = useState<string>(
    String(defaultLabelCount(outstanding || line.qty)),
  );
  const [labelTouched, setLabelTouched] = useState(false);

  useEffect(() => {
    if (!offersLabels || labelTouched) return;
    setLabelCount(String(defaultLabelCount(Number(receivedQty))));
  }, [offersLabels, labelTouched, receivedQty]);

  // License-key inventory: when this line's SKU is a resale-license product we
  // mint one key per unit received. Auto-detected from the product catalog;
  // the buyer can still toggle it on manually if the flag isn't set.
  const [isLicenseLine, setIsLicenseLine] = useState(false);
  const [licenseKeysText, setLicenseKeysText] = useState('');

  useEffect(() => {
    if (!line.sku) return;
    let active = true;
    getProducts()
      .then(({ items }) => {
        if (!active) return;
        const product = items.find((p) => p.sku === line.sku);
        if (product?.isLicenseKey) setIsLicenseLine(true);
      })
      .catch(() => {
        /* catalog optional — fall back to the manual toggle */
      });
    return () => {
      active = false;
    };
  }, [line.sku]);

  const parsedKeys = useMemo(() => parseKeys(licenseKeysText), [licenseKeysText]);
  const qtyNum = Number(receivedQty) || 0;
  const keysMismatch = isLicenseLine && parsedKeys.length > 0 && parsedKeys.length !== qtyNum;

  const canSubmit = useMemo(() => {
    const qty = Number(receivedQty);
    if (!Number.isFinite(qty) || qty <= 0 || submitting) return false;
    if (isLicenseLine && parsedKeys.length !== qty) return false;
    return true;
  }, [receivedQty, submitting, isLicenseLine, parsedKeys.length]);

  // Guard close while a submit is in flight so the panel doesn't unmount
  // mid-request (Cancel, Escape, and the × close all route through this).
  const handleClose = useCallback(() => {
    if (!submitting) onClose(false);
  }, [submitting, onClose]);

  // Escape closes the panel. (When the DateField is open it consumes Escape and
  // stops propagation, so only the picker closes.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const qty = Number(receivedQty);
      if (!Number.isFinite(qty) || qty <= 0) {
        throw new Error('Received qty must be a positive number');
      }
      const request: ReceiveLineRequest = {
        received_qty: qty,
        received_date: receivedDate || undefined,
      };
      if (actualUnitCost !== '') {
        const cost = Number(actualUnitCost);
        if (!Number.isFinite(cost)) {
          throw new Error('Actual unit cost must be a number');
        }
        request.actual_unit_cost = cost;
      }
      if (isLicenseLine) {
        if (parsedKeys.length !== qty) {
          throw new Error(
            `Enter exactly ${qty} license key(s) — one per line (got ${parsedKeys.length})`,
          );
        }
        request.license_keys = parsedKeys;
      }
      if (offersLabels) {
        // Clamp to 0..min(qty, MAX) so we never print more labels than parts.
        const raw = Number(labelCount);
        const labels = Number.isFinite(raw)
          ? Math.max(0, Math.min(Math.floor(raw), qty, MAX_RECEIPT_LABELS))
          : 0;
        request.label_count = labels;
      }
      await receiveLine(line.id, request);
      onClose(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record receipt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SidePanel
      title="Record Receipt"
      submitLabel={submitting ? 'Recording…' : 'Record Receipt'}
      isValid={canSubmit}
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

      <div className="receive-summary">
        <div className="receive-summary__title">{line.description}</div>
        <div className="receive-summary__grid">
          <span className="receive-summary__label">Vendor</span>
          <span className="receive-summary__value" title={line.vendorName || undefined}>
            {line.vendorName || '—'}
          </span>
          <span className="receive-summary__label">SKU</span>
          <span className="receive-summary__value" title={line.sku || undefined}>
            {line.sku || '—'}
          </span>
          <span className="receive-summary__label">Ordered</span>
          <span className="receive-summary__value">{line.qty}</span>
          {line.receivedQty > 0 && (
            <>
              <span className="receive-summary__label">Received</span>
              <span className="receive-summary__value">{line.receivedQty}</span>
            </>
          )}
        </div>
      </div>

      <div className="side-panel__field-row">
        <FormField label="Received Qty *">
          <input
            type="text"
            inputMode="numeric"
            value={receivedQty}
            onChange={(e) => setReceivedQty(e.target.value)}
            style={{ textAlign: 'right' }}
            autoFocus
          />
        </FormField>
        <FormField label="Actual Unit Cost">
          <input
            type="text"
            inputMode="decimal"
            value={actualUnitCost}
            onChange={(e) => setActualUnitCost(e.target.value)}
            placeholder="0.00"
            style={{ textAlign: 'right' }}
          />
        </FormField>
      </div>

      <DateField
        label="Received Date"
        value={receivedDate || null}
        onChange={(iso) => setReceivedDate(iso ?? '')}
      />

      {offersLabels && (
        <div className="receive-field">
          <FormField label="Print labels">
            <input
              type="text"
              inputMode="numeric"
              value={labelCount}
              onChange={(e) => {
                setLabelTouched(true);
                setLabelCount(e.target.value);
              }}
              style={{ textAlign: 'right' }}
            />
          </FormField>
          <span className="receive-hint">
            Ticket labels to print — one per unit (max {MAX_RECEIPT_LABELS}). 0 = don't print.
          </span>
        </div>
      )}

      <label className="side-panel__checkbox">
        <input
          type="checkbox"
          checked={isLicenseLine}
          onChange={(e) => setIsLicenseLine(e.target.checked)}
        />
        These are license keys — add them to inventory
      </label>

      {isLicenseLine && (
        <div className="receive-field">
          <FormField label="License keys (one per line)">
            <textarea
              rows={4}
              value={licenseKeysText}
              onChange={(e) => setLicenseKeysText(e.target.value)}
              placeholder={'XXXXX-XXXXX-XXXXX-XXXXX-XXXXX\n…'}
            />
          </FormField>
          <span className={`receive-hint${keysMismatch ? ' receive-hint--error' : ''}`}>
            {parsedKeys.length} key(s) entered — must equal received qty ({qtyNum})
          </span>
        </div>
      )}
    </SidePanel>
  );
};
